import {
  ActionIcon,
  Button,
  Group,
  Loader,
  ScrollArea,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { useMantineColorScheme } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { Trash2Icon } from 'lucide-react';
import { ScoutChatCloseConfirmModal } from './ScoutChatCloseConfirmModal';
import { useScoutChat } from './useScoutChat';
import { ScoutChatHeader } from './components/ScoutChatHeader';
import { ScoutChatMessage } from './components/ScoutChatMessage';
import { ScoutChatInput } from './components/ScoutChatInput';
import { ScoutChatEmptyState } from './components/ScoutChatEmptyState';
import { ScoutChatTransitionIndicator } from './components/ScoutChatTransitionIndicator';
import { useScoutChatContext } from './ScoutChatContext';
import { SCOUT_IS_UNDER_MAINTENANCE } from './scoutMaintenance';

interface ScoutChatProps {
  opened: boolean;
  onClose: () => void;
  initialText?: string;
  showFullScreenContinuationNotice?: boolean;
}

interface ConversationDeleteActionProps {
  label: string;
  conversationId: string;
  disabled: boolean;
  isConfirming: boolean;
  isDeleting: boolean;
  onStartConfirm: (conversationId: string) => void;
  onCancelConfirm: () => void;
  onConfirmDelete: (conversationId: string) => Promise<void>;
}

const ConversationDeleteAction = ({
  label,
  conversationId,
  disabled,
  isConfirming,
  isDeleting,
  onStartConfirm,
  onCancelConfirm,
  onConfirmDelete,
}: ConversationDeleteActionProps) => {
  return (
    <Group gap={4} wrap='nowrap'>
      {isConfirming ? (
        <>
          <Button
            size='compact-xs'
            variant='subtle'
            disabled={isDeleting}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onCancelConfirm();
            }}
          >
            Cancel
          </Button>
          <Button
            size='compact-xs'
            color='red'
            loading={isDeleting}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={async (event) => {
              event.preventDefault();
              event.stopPropagation();
              await onConfirmDelete(conversationId);
            }}
          >
            Delete
          </Button>
        </>
      ) : (
        <ActionIcon
          variant='subtle'
          color='gray'
          size='sm'
          aria-label={`Delete chat ${label}`}
          disabled={disabled}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onStartConfirm(conversationId);
          }}
        >
          <Trash2Icon size={14} />
        </ActionIcon>
      )}
    </Group>
  );
};

export const ScoutChat = ({
  opened,
  onClose,
  initialText,
  showFullScreenContinuationNotice = false,
}: ScoutChatProps) => {
  const NEW_CHAT_OPTION_VALUE = '__new_chat__';
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const context = useScoutChatContext();
  const [showTransitionIndicator, setShowTransitionIndicator] = useState(false);
  const [confirmingDeleteConversationId, setConfirmingDeleteConversationId] =
    useState<string | null>(null);
  const [deletingConversationId, setDeletingConversationId] = useState<
    string | null
  >(null);
  const wasOpenedRef = useRef(false);

  const {
    input,
    messages,
    showCloseConfirm,
    isLoading,
    isConversationLoading,
    conversations,
    activeConversationId,
    scrollViewportRef,
    textareaRef,
    canSend,
    setInput,
    handleSend,
    handleCancel,
    handleCloseAttempt,
    handleConfirmClose,
    handleCancelClose,
    handleSelectConversation,
    handleStartNewConversation,
    handleDeleteConversation,
    handlePrepareFreshChat,
  } = useScoutChat({ opened, initialText });

  useEffect(() => {
    const justOpened = opened && !wasOpenedRef.current;
    if (justOpened && !showFullScreenContinuationNotice) {
      handlePrepareFreshChat();
    }
    wasOpenedRef.current = opened;
  }, [opened, showFullScreenContinuationNotice, handlePrepareFreshChat]);

  // Show transition indicator when drawer opens with existing messages
  useEffect(() => {
    if (
      opened &&
      showFullScreenContinuationNotice &&
      context.hasActiveChat &&
      messages.length > 0
    ) {
      setShowTransitionIndicator(true);
      // Hide after 5 seconds
      const timer = setTimeout(() => {
        setShowTransitionIndicator(false);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setShowTransitionIndicator(false);
    }
  }, [
    opened,
    showFullScreenContinuationNotice,
    context.hasActiveChat,
    messages.length,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading) {
        handleSend();
      }
    }
  };

  const finalOnClose = () => {
    handleConfirmClose();
    onClose();
  };

  const handleHeaderClose = () => {
    if (input.trim().length > 0) {
      handleCloseAttempt();
    } else {
      finalOnClose();
    }
  };

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
      <div
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: `1px solid var(--mantine-color-gray-3)`,
          backgroundColor: 'var(--mantine-color-body)',
          ...(isDark
            ? {
                background:
                  'linear-gradient(to bottom, var(--mantine-color-dark-9), var(--mantine-color-dark-6))',
                color: 'var(--mantine-color-dark-0)',
              }
            : {
                background: `radial-gradient(circle at top left, #ffffff, #eeeefd, #ffffff, #ffffff), radial-gradient(circle at bottom right, #fdeffc, #ffffff, #ffffff)`,
                backgroundSize: '200% 200%',
                animation: 'gradientShift 30s ease infinite',
              }),
        }}
      >
        <ScoutChatHeader showCloseButton={true} onClose={handleHeaderClose} />
        {!SCOUT_IS_UNDER_MAINTENANCE && (
          <Group px='md' py='xs' gap='xs' align='center' wrap='nowrap'>
            <Select
              data={[
                {
                  value: NEW_CHAT_OPTION_VALUE,
                  label: 'New chat',
                },
                ...conversations.map((conversation) => ({
                  value: conversation.id,
                  label: conversation.title,
                })),
              ]}
              value={activeConversationId}
              placeholder='Select a chat'
              onDropdownClose={() => {
                setConfirmingDeleteConversationId(null);
              }}
              onChange={(value) => {
                setConfirmingDeleteConversationId(null);
                if (!value) {
                  return;
                }
                if (value === NEW_CHAT_OPTION_VALUE) {
                  handleStartNewConversation();
                  return;
                }
                if (value !== activeConversationId) {
                  handleSelectConversation(value);
                }
              }}
              style={{ flex: 1 }}
              size='xs'
              searchable
              nothingFoundMessage='No chats yet'
              disabled={isLoading || isConversationLoading}
              renderOption={(item: any) => {
                const option = item.option ?? item;
                const isCreateOption = option.value === NEW_CHAT_OPTION_VALUE;
                return (
                  <Group
                    justify='space-between'
                    wrap='nowrap'
                    gap='xs'
                    style={{ width: '100%' }}
                  >
                    <Text size='sm' style={{ flex: 1 }}>
                      {option.label}
                    </Text>
                    {!isCreateOption && (
                      <ConversationDeleteAction
                        label={option.label}
                        conversationId={option.value}
                        disabled={isLoading || isConversationLoading}
                        isConfirming={
                          confirmingDeleteConversationId === option.value
                        }
                        isDeleting={deletingConversationId === option.value}
                        onStartConfirm={(conversationId) => {
                          setConfirmingDeleteConversationId(conversationId);
                        }}
                        onCancelConfirm={() => {
                          setConfirmingDeleteConversationId(null);
                        }}
                        onConfirmDelete={async (conversationId) => {
                          setDeletingConversationId(conversationId);
                          try {
                            await handleDeleteConversation(conversationId);
                          } finally {
                            setDeletingConversationId(null);
                            setConfirmingDeleteConversationId(null);
                          }
                        }}
                      />
                    )}
                  </Group>
                );
              }}
            />
            {isConversationLoading && (
              <Loader size='xs' color='pink.4' type='dots' />
            )}
          </Group>
        )}

        <ScrollArea
          style={{ flex: 1, minHeight: 0 }}
          viewportRef={scrollViewportRef}
          offsetScrollbars
          scrollbarSize={6}
        >
          <Stack
            gap='sm'
            p='md'
            style={{ paddingBottom: 'var(--mantine-spacing-xl)' }}
          >
            {showTransitionIndicator && (
              <ScoutChatTransitionIndicator messageCount={messages.length} />
            )}
            {SCOUT_IS_UNDER_MAINTENANCE ? null : messages.length === 0 ? (
              <ScoutChatEmptyState />
            ) : (
              messages.map((m) => {
                const isThinking = m.content === 'Thinking...';
                const isToolCall =
                  m.content === 'Looking that up...' ||
                  m.content === 'Creating...' ||
                  m.content.startsWith('Checking ') ||
                  m.content.startsWith('Creating ');
                const shouldShimmer = isThinking || isToolCall;
                return (
                  <ScoutChatMessage
                    key={m.id}
                    message={m}
                    isThinking={isThinking}
                    isToolCall={isToolCall}
                    shouldShimmer={shouldShimmer}
                  />
                );
              })
            )}
          </Stack>
        </ScrollArea>

        {!SCOUT_IS_UNDER_MAINTENANCE && (
          <div
            style={{
              padding: 'var(--mantine-spacing-md)',
              borderTop: '1px solid var(--mantine-color-gray-2)',
              backgroundColor: 'var(--mantine-color-body)',
              flexShrink: 0,
            }}
          >
            <ScoutChatInput
              input={input}
              isLoading={isLoading}
              canSend={canSend}
              textareaRef={textareaRef}
              placeholder={
                isLoading ? 'Scout is working...' : 'Ask a question...'
              }
              onInputChange={setInput}
              onSend={handleSend}
              onCancel={handleCancel}
              onKeyDown={handleKeyDown}
            />
          </div>
        )}
      </div>

      <ScoutChatCloseConfirmModal
        opened={showCloseConfirm}
        onClose={handleCancelClose}
        onConfirm={finalOnClose}
      />
    </>
  );
};

export default ScoutChat;
